import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useInView, useScroll, useTransform } from "framer-motion";
import {
  Users,
  Stethoscope,
  UserCheck,
  Activity,
  ShieldCheck,
  CalendarDays,
  FileText,
  BarChart3,
} from "lucide-react";

import clinicPortalImg from "../assets/clinic-portal.webp";
import staffPortalImg from "../assets/portal-staff.webp";
import clientPortalImg from "../assets/client-portal.webp";

const services = [
  {
    id: "admin-portal",
    tag: "Admin",
    title: "Admin Portal",
    icon: Stethoscope,
    accentColor: "#3b82f6",
    accentLight: "#eff6ff",
    accentDark: "#1d4ed8",
    url: "vetassist.app/admin-portal",
    description:
      "Oversee staff, manage schedules, monitor clinic activity, and review operational reports — all from a single command centre.",
    image: clinicPortalImg,
    highlights: [
      { icon: FileText, text: "Electronic Medical Records" },
      { icon: ShieldCheck, text: "Digital Certificates" },
      { icon: Activity, text: "Smart Prescription" },
      { icon: BarChart3, text: "Track inventory by portion with better accuracy" },
    ],
  },
  {
    id: "staff-portal",
    tag: "Operations",
    title: "Staff Portal",
    icon: UserCheck,
    accentColor: "#22c55e",
    accentLight: "#f0fdf4",
    accentDark: "#15803d",
    url: "vetassist.app/staff-portal",
    description:
      "Handle appointments, update pet records, record treatments, and assist veterinarians during daily operations.",
    image: staffPortalImg,
    highlights: [
      { icon: Activity, text: "Clock in & Clock out / Time Tracker" },
      { icon: ShieldCheck, text: "Role based access control" },
      { icon: CalendarDays, text: "Set time permissions for both in clinic and off site work" },
    ],
  },
  {
    id: "pet-owner-portal",
    tag: "Pet-Owner-Facing",
    title: "Pet Owner Portal",
    icon: Users,
    accentColor: "#3b82f6",
    accentLight: "#eff6ff",
    accentDark: "#1d4ed8",
    url: "vetassist.app/pet-owner-portal",
    description:
      "View pets, book appointments, track visits, access medical records, and monitor clinic transactions in one place.",
    image: clientPortalImg,
    highlights: [
      { icon: FileText, text: "Full vaccine history access" },
      { icon: CalendarDays, text: "Self service appointment booking" },
      { icon: Activity, text: "Transaction & billing tracker" },
    ],
  },
];

const slideLeft = {
  hidden: { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: [0.25, 1, 0.5, 1] } },
  exit: { opacity: 0, x: 16, transition: { duration: 0.25, ease: [0.25, 1, 0.5, 1] } },
};

const slideRight = {
  hidden: { opacity: 0, x: 24 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: [0.25, 1, 0.5, 1], delay: 0.05 },
  },
  exit: { opacity: 0, x: -16, transition: { duration: 0.25, ease: [0.25, 1, 0.5, 1] } },
};

const Services = () => {
  const [activeId, setActiveId] = useState(services[0].id);
  const [isPaused, setIsPaused] = useState(false);
  const [userSelected, setUserSelected] = useState(false);
  const active = services.find((s) => s.id === activeId);

  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const bodyRef = useRef(null);

  const headerInView = useInView(headerRef, { once: true, margin: "-60px" });
  const bodyInView = useInView(bodyRef, { once: true, margin: "-40px" });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const blobLeftX = useTransform(scrollYProgress, [0, 1], [-30, 30]);
  const blobRightX = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const blobY = useTransform(scrollYProgress, [0, 1], [-20, 20]);
  const gridOpacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setActiveId((current) => {
        const currentIndex = services.findIndex((s) => s.id === current);
        const nextIndex = (currentIndex + 1) % services.length;
        return services[nextIndex].id;
      });
    }, 3500);
    return () => clearInterval(interval);
  }, [isPaused]);

  return (
    <div
      ref={sectionRef}
      className="relative w-full overflow-hidden font-sans text-slate-900 selection:bg-blue-100">
      {/* ── Blobs with parallax ── */}
      <motion.div style={{ x: blobLeftX, y: blobY }} className="pointer-events-none absolute">
        <div
          style={{
            width: 750,
            height: 750,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(59,130,246,0.55) 0%, transparent 58%)",
            filter: "blur(1px)",
            position: "relative",
            left: "-22vw",
            top: "5vh",
          }}
        />
      </motion.div>

      <motion.div
        style={{ x: blobRightX, y: blobY }}
        className="pointer-events-none absolute top-0 right-0">
        <div
          style={{
            width: 700,
            height: 700,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(34,197,94,0.48) 0%, transparent 58%)",
            filter: "blur(1px)",
            position: "relative",
            right: "-22vw",
            top: "2vh",
          }}
        />
      </motion.div>

      {/* ── Portal grid ── */}
      <motion.div
        style={{ opacity: gridOpacity }}
        className="pointer-events-none absolute inset-0 h-full w-full">
        <div
          className="h-full w-full"
          style={{
            backgroundImage: `
              linear-gradient(rgba(71,85,105,0.07) 1px, transparent 1px),
              linear-gradient(90deg, rgba(71,85,105,0.07) 1px, transparent 1px)
            `,
            backgroundSize: "60px 60px",
            maskImage: `radial-gradient(ellipse 70% 60% at 50% 30%, black 0%, transparent 100%)`,
            WebkitMaskImage: `radial-gradient(ellipse 70% 60% at 50% 30%, black 0%, transparent 100%)`,
          }}
        />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 pt-10 pb-16 sm:px-6 sm:pt-14 sm:pb-24 md:px-8 lg:px-12 lg:pt-20 lg:pb-36 xl:px-16">
        {/* ── Header ── */}
        <div ref={headerRef}>
          <motion.header
            className="mx-auto mb-8 max-w-3xl text-center sm:mb-12"
            initial={{ opacity: 0, y: 32 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.25, 1, 0.5, 1] }}>
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-5xl md:text-6xl">
              <motion.span
                className="block text-slate-900"
                initial={{ opacity: 0, y: 20 }}
                animate={headerInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1], delay: 0.1 }}>
                Everything your clinic needs,
              </motion.span>
              <motion.span
                className="block bg-linear-to-r from-[#22c55e] via-[#3b82f6] to-[#60a5fa] bg-clip-text text-transparent"
                initial={{ opacity: 0, y: 20 }}
                animate={headerInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1], delay: 0.2 }}>
                all in one platform.
              </motion.span>
            </h2>

            <motion.p
              className="mt-4 text-sm leading-relaxed text-slate-500 sm:mt-5 sm:text-lg"
              initial={{ opacity: 0, y: 16 }}
              animate={headerInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1], delay: 0.3 }}>
              Three powerful portals working in harmony, built for admins, staff, and pet owners.
            </motion.p>
          </motion.header>
        </div>

        {/* ── Tabs + panel ── */}
        <div ref={bodyRef}>
          {/* ── TABS: Mobile = compact 3-col icon+label ── */}
          <div
            className="mb-3 grid grid-cols-3 gap-2 sm:hidden"
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => {
              if (!userSelected) setIsPaused(false);
            }}>
            {services.map((s) => {
              const isActive = s.id === activeId;
              return (
                <button
                  key={s.id + "-mobile"}
                  onClick={() => {
                    setActiveId(s.id);
                    setUserSelected(true);
                    setIsPaused(true);
                  }}
                  className="relative flex flex-col items-center justify-center gap-1.5 overflow-hidden rounded-xl px-2 py-3 transition-all duration-200 focus:outline-none active:scale-95"
                  style={{
                    background: isActive
                      ? `linear-gradient(145deg, ${s.accentColor}18, ${s.accentColor}06, rgba(255,255,255,0.95))`
                      : "linear-gradient(145deg, rgba(255,255,255,0.95), rgba(241,245,249,0.80))",
                    border: `1.5px solid ${isActive ? s.accentColor + "45" : "rgba(226,232,240,0.8)"}`,
                    boxShadow: isActive
                      ? `0 0 0 1px ${s.accentColor}15, 0 6px 20px ${s.accentColor}20, 0 2px 6px rgba(0,0,0,0.05)`
                      : "0 2px 8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,1)",
                  }}>
                  {/* Active top bar */}
                  {isActive && (
                    <div
                      className="absolute top-0 right-0 left-0 h-[2.5px] rounded-t-xl"
                      style={{
                        background: `linear-gradient(90deg, ${s.accentColor}, ${s.accentColor}70)`,
                        boxShadow: `0 0 8px ${s.accentColor}80`,
                      }}
                    />
                  )}
                  {/* Icon */}
                  <div
                    className="flex h-7 w-7 items-center justify-center rounded-lg"
                    style={{
                      background: isActive
                        ? `linear-gradient(135deg, ${s.accentColor}25, ${s.accentColor}10)`
                        : "rgba(241,245,249,0.8)",
                      boxShadow: isActive ? `0 0 10px ${s.accentColor}30` : "none",
                    }}>
                    <s.icon
                      className="h-3.5 w-3.5"
                      strokeWidth={2.5}
                      style={{ color: isActive ? s.accentColor : "#94a3b8" }}
                    />
                  </div>
                  {/* Label */}
                  <span
                    className="text-center text-[11px] leading-tight font-bold whitespace-pre-line"
                    style={{ color: isActive ? "#0f172a" : "#94a3b8" }}>
                    {s.title}
                  </span>
                  {/* Dot */}
                  <div
                    className="h-1 rounded-full transition-all duration-300"
                    style={{
                      width: isActive ? 18 : 5,
                      background: isActive ? s.accentColor : "#cbd5e1",
                    }}
                  />
                </button>
              );
            })}
          </div>

          {/* ── DESKTOP: Card-style tab grid ── */}
          <div
            className="mb-4 hidden grid-cols-3 gap-3 sm:grid"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => {
              if (!userSelected) setIsPaused(false);
            }}>
            {services.map((s, i) => {
              const isActive = s.id === activeId;
              return (
                <motion.button
                  key={s.id}
                  onClick={() => {
                    setActiveId(s.id);
                    setUserSelected(true);
                    setIsPaused(true);
                  }}
                  whileHover={{ y: -4, scale: 1.02 }}
                  whileTap={{ y: 0, scale: 0.98 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={bodyInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1], delay: 0.1 + i * 0.08 }}
                  className="group relative cursor-pointer overflow-hidden rounded-2xl border px-6 py-5 text-center focus:outline-none"
                  style={{
                    background: isActive
                      ? `linear-gradient(145deg, ${s.accentColor}22, ${s.accentColor}08, rgba(255,255,255,0.95))`
                      : "linear-gradient(145deg, rgba(255,255,255,0.95), rgba(241,245,249,0.80))",
                    borderColor: isActive ? `${s.accentColor}50` : "rgba(255,255,255,0.90)",
                    boxShadow: isActive
                      ? `0 0 0 1px ${s.accentColor}25, 0 12px 40px ${s.accentColor}25, 0 4px 12px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)`
                      : "0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,1)",
                    transition: "border-color 0.25s, box-shadow 0.25s, background 0.25s",
                  }}>
                  <div
                    className="pointer-events-none absolute top-0 right-0 left-0 h-px"
                    style={{
                      background: isActive
                        ? `linear-gradient(90deg, transparent, ${s.accentColor}60, transparent)`
                        : "linear-gradient(90deg, transparent, rgba(255,255,255,1), transparent)",
                    }}
                  />
                  {isActive && (
                    <motion.div
                      layoutId="step-bar"
                      className="absolute top-0 right-0 left-0 h-0.75 rounded-t-2xl"
                      style={{
                        background: `linear-gradient(90deg, ${s.accentColor}, ${s.accentColor}60)`,
                        boxShadow: `0 0 10px ${s.accentColor}90`,
                      }}
                      transition={{ duration: 0.28, ease: [0.25, 1, 0.5, 1] }}
                    />
                  )}
                  <div
                    className="pointer-events-none absolute inset-0 rounded-2xl"
                    style={{
                      background: isActive
                        ? `radial-gradient(ellipse at 50% 0%, ${s.accentColor}12, transparent 65%)`
                        : "radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.6), transparent 65%)",
                    }}
                  />
                  <div
                    className="relative mx-auto mb-2.5 flex h-9 w-9 items-center justify-center rounded-xl"
                    style={{
                      background: isActive
                        ? `linear-gradient(135deg, ${s.accentColor}28, ${s.accentColor}10)`
                        : "linear-gradient(135deg, rgba(255,255,255,0.9), rgba(241,245,249,0.8))",
                      boxShadow: isActive
                        ? `0 0 16px ${s.accentColor}35, inset 0 1px 0 rgba(255,255,255,0.6)`
                        : "0 2px 6px rgba(0,0,0,0.07), inset 0 1px 0 rgba(255,255,255,1)",
                      transition: "background 0.25s, box-shadow 0.25s",
                    }}>
                    <s.icon
                      className="h-4 w-4"
                      strokeWidth={2}
                      style={{
                        color: isActive ? s.accentColor : "#94a3b8",
                        transition: "color 0.25s",
                      }}
                    />
                  </div>
                  <p
                    className="relative text-base font-bold"
                    style={{ color: isActive ? "#0f172a" : "#64748b", transition: "color 0.25s" }}>
                    {s.title}
                  </p>
                  <div className="mt-2 flex justify-center">
                    <motion.div
                      className="h-1 rounded-full"
                      animate={{
                        width: isActive ? 24 : 6,
                        backgroundColor: isActive ? s.accentColor : "#cbd5e1",
                        boxShadow: isActive ? `0 0 6px ${s.accentColor}80` : "0 0 0px transparent",
                      }}
                      transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
                    />
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* ── Main panel ── */}
          <motion.div
            className="relative overflow-hidden rounded-2xl sm:rounded-3xl"
            initial={{ opacity: 0, y: 32 }}
            animate={bodyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.25, 1, 0.5, 1], delay: 0.35 }}
            style={{
              background: "linear-gradient(145deg, rgba(255,255,255,0.95), rgba(248,250,252,0.90))",
              border: `1px solid rgba(255,255,255,0.90)`,
              boxShadow: `
                0 0 0 1px ${active.accentColor}15,
                0 24px 64px rgba(0,0,0,0.10),
                0 8px 24px rgba(0,0,0,0.06),
                0 2px 6px rgba(0,0,0,0.04),
                inset 0 1px 0 rgba(255,255,255,1),
                inset 0 -1px 0 rgba(0,0,0,0.02)
              `,
              transition: "box-shadow 0.4s ease",
            }}>
            {/* Top glass sheen */}
            <div
              className="pointer-events-none absolute top-0 right-0 left-0 z-10 h-px"
              style={{
                background: `linear-gradient(90deg, transparent, rgba(255,255,255,1) 30%, rgba(255,255,255,1) 70%, transparent)`,
              }}
            />

            {/* Accent glow */}
            <div
              className="pointer-events-none absolute inset-0 rounded-2xl transition-all duration-700 sm:rounded-3xl"
              style={{
                background: `radial-gradient(ellipse at 15% 55%, ${active.accentColor}08 0%, transparent 55%)`,
              }}
            />

            <AnimatePresence mode="wait">
              <motion.div
                key={activeId}
                className="relative flex flex-col lg:grid lg:grid-cols-2"
                initial="hidden"
                animate="visible"
                exit="exit">
                {/* ── Screenshot panel ── */}
                <motion.div
                  variants={slideLeft}
                  className="relative flex items-center justify-center overflow-hidden border-b border-slate-100 bg-slate-50/70 lg:border-r lg:border-b-0"
                  style={{ padding: "clamp(16px, 4vw, 32px)" }}>
                  {/* Corner glows */}
                  <div
                    className="pointer-events-none absolute top-0 left-0 h-32 w-32"
                    style={{
                      background: `radial-gradient(circle at 0% 0%, ${active.accentColor}16, transparent 70%)`,
                    }}
                  />
                  <div
                    className="pointer-events-none absolute right-0 bottom-0 h-24 w-24"
                    style={{
                      background: `radial-gradient(circle at 100% 100%, ${active.accentColor}10, transparent 70%)`,
                    }}
                  />

                  {/* Browser mock */}
                  <div
                    className="relative w-full overflow-hidden rounded-xl border border-slate-200 bg-white sm:rounded-2xl"
                    style={{
                      maxWidth: 520,
                      boxShadow: `0 12px 48px ${active.accentColor}1c, 0 2px 10px rgba(0,0,0,0.07)`,
                    }}>
                    {/* Browser chrome */}
                    <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-3 py-2 sm:gap-3 sm:px-4 sm:py-2.5">
                      <div className="flex gap-1 sm:gap-1.5">
                        {["#f87171", "#fbbf24", "#4ade80"].map((c) => (
                          <div
                            key={c}
                            className="h-1.5 w-1.5 rounded-full sm:h-2 sm:w-2"
                            style={{ background: c }}
                          />
                        ))}
                      </div>
                      <div className="flex min-w-0 flex-1 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[9px] text-slate-400 sm:gap-1.5 sm:px-2.5 sm:py-1 sm:text-[10px]">
                        <ShieldCheck
                          className="h-2 w-2 shrink-0 text-emerald-500 sm:h-2.5 sm:w-2.5"
                          strokeWidth={2.5}
                        />
                        <span className="truncate">{active.url}</span>
                      </div>
                      <span
                        className="hidden shrink-0 rounded-md px-2 py-0.5 text-[9px] font-semibold sm:block"
                        style={{ background: active.accentLight, color: active.accentDark }}>
                        {active.tag}
                      </span>
                    </div>

                    {/* Screenshot — taller on mobile for better visibility */}
                    <div
                      className="overflow-hidden"
                      style={{ maxHeight: "clamp(160px, 40vw, 340px)" }}>
                      <img
                        src={active.image}
                        alt={active.title}
                        className="block w-full object-cover object-top"
                      />
                    </div>
                  </div>
                </motion.div>

                {/* ── Content panel ── */}
                <motion.div
                  variants={slideRight}
                  className="flex flex-col justify-center px-5 py-6 sm:px-8 sm:py-10 lg:px-12 lg:py-12">
                  {/* Icon + title */}
                  <div className="mb-3 flex flex-col items-center gap-2 sm:mb-4 sm:gap-3">
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-xl sm:h-12 sm:w-12 sm:rounded-2xl"
                      style={{
                        background: `linear-gradient(135deg, ${active.accentColor}25, ${active.accentColor}10)`,
                        boxShadow: `0 0 20px ${active.accentColor}35, inset 0 1px 0 rgba(255,255,255,0.6)`,
                        border: `1px solid ${active.accentColor}25`,
                      }}>
                      <active.icon
                        className="h-5 w-5 sm:h-6 sm:w-6"
                        strokeWidth={2}
                        style={{ color: active.accentColor }}
                      />
                    </div>

                    <h3
                      className="text-center text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl"
                      style={{
                        background: `linear-gradient(135deg, ${active.accentColor}, ${active.accentDark})`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        filter: `drop-shadow(0 0 18px ${active.accentColor}55) drop-shadow(0 0 6px ${active.accentColor}33)`,
                      }}>
                      {active.title}
                    </h3>
                  </div>

                  <p className="mb-5 text-center text-sm leading-relaxed text-slate-500 sm:mb-8 sm:text-[15px]">
                    {active.description}
                  </p>

                  {/* Highlights */}
                  <ul className="flex flex-col gap-2 sm:gap-3">
                    {active.highlights.map(({ icon: HIcon, text }, i) => (
                      <motion.li
                        key={text}
                        className="flex items-center gap-2 sm:gap-3"
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: i * 0.07, ease: [0.25, 1, 0.5, 1] }}>
                        <div
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg sm:h-8 sm:w-8 sm:rounded-xl"
                          style={{
                            background: `linear-gradient(135deg, ${active.accentColor}20, ${active.accentColor}08)`,
                            boxShadow: `0 2px 8px ${active.accentColor}20, inset 0 1px 0 rgba(255,255,255,0.8)`,
                            border: `1px solid ${active.accentColor}18`,
                          }}>
                          <HIcon
                            className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                            strokeWidth={2}
                            style={{ color: active.accentColor }}
                          />
                        </div>
                        <span className="text-xs font-medium text-slate-700 sm:text-sm">
                          {text}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Services;
